// Shared visual language for the analytics dashboard demo (docs/analytics-dashboard-strategy.md §3).
// Every colour is paired with a label — colour alone must never carry the meaning.
//
// Two states, never three: a vehicle past its promised time is "Delayed"; everything else
// carries no label at all. Escalations are Open/Closed.
// There is no "on time" or "at risk" badge — nothing shown means fine.

export const DELAYED = { label: "Delayed", text: "text-red-700", bg: "bg-red-50", border: "border-red-200", dot: "bg-red-500", hex: "#D6322A" };

export const ESCALATION_STATUS = {
  open: { label: "Open", text: "text-red-700", bg: "bg-red-50", border: "border-red-200", dot: "bg-red-500" },
  closed: { label: "Closed", text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", dot: "bg-emerald-500" },
};

export const ESCALATION_SOURCE_LABEL = {
  classifier: "Classifier-raised",
  sla_breach: "SLA-raised",
  manual: "Manual",
  staff_tone: "Staff Conduct",
};

export const NOTIFICATION_TYPE_LABEL = {
  escalation: "Escalation",
  sla_breach: "Delivery delay",
  sentiment_pattern: "Sentiment pattern",
  digest: "Digest",
};

// Numbers get tabular-nums on the app's existing body face (IBM Plex Sans) — this aligns
// columns without reading as machine-like the way a monospace face would (§3). Do not use
// a monospace font anywhere in this app.
export const NUM = "tabular-nums";

// Card shell matches the existing app rather than inventing a new one.
export const CARD = "rounded-xl border border-slate-200 bg-white p-5";

// --- formatting helpers — use these everywhere, never format inline ------------------

// ₹1,23,456 — Indian digit grouping, not ₹123,456.
export function formatRupees(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
}

// "Today, 4:30 PM" / "Yesterday, 11:00 AM" / "8 Aug, 2:15 PM" — 12-hour with AM/PM,
// never a bare ISO timestamp on screen.
export function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const time = d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
  const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (isSameDay(d, now)) return `Today, ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(d, yesterday)) return `Yesterday, ${time}`;
  const datePart = d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  return `${datePart}, ${time}`;
}

// "2 hours 10 mins" — in words, never a bare number or a duration code.
export function formatWaited(mins) {
  if (mins == null || Number.isNaN(mins)) return "—";
  const totalMins = Math.max(0, Math.round(mins));
  const days = Math.floor(totalMins / 1440);
  const hours = Math.floor((totalMins % 1440) / 60);
  const remMins = totalMins % 60;
  const parts = [];
  if (days > 0) parts.push(`${days} day${days === 1 ? "" : "s"}`);
  if (hours > 0) parts.push(`${hours} hour${hours === 1 ? "" : "s"}`);
  if (days === 0 && remMins > 0) parts.push(`${remMins} min${remMins === 1 ? "" : "s"}`);
  if (parts.length === 0) return "Just now";
  return parts.slice(0, 2).join(" ");
}
